import React, { useState, useEffect } from "react";
import { Table, Button, Card, Form, Alert, Pagination } from "react-bootstrap";
import { ingredientsApi } from "../services/services";
import { Ingredient } from "../types";
import { SlidePanel, TableControls } from "../components/shared";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";

const Ingredients: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEditId, setCurrentEditId] = useState<number | null>(null);

  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [ingredientName, setIngredientName] = useState("");
  const [ingredientCode, setIngredientCode] = useState("");

  useEffect(() => {
    fetchIngredients();

    const onSupplierChange = () => fetchIngredients();
    window.addEventListener("mockSupplierChanged", onSupplierChange);
    return () => window.removeEventListener("mockSupplierChanged", onSupplierChange);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, entriesPerPage]);

  const fetchIngredients = () => {
    setLoading(true);
    ingredientsApi
      .getIngredients()
      .then(setIngredients)
      .catch(() => setError("Failed to fetch ingredients"))
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setIngredientName("");
    setIngredientCode("");
    setIsEditing(false);
    setCurrentEditId(null);
  };

  const handleCancel = () => {
    resetForm();
    setModalOpen(false);
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleEdit = (ingredient: Ingredient) => {
    setIngredientName(ingredient.name);
    setIngredientCode(ingredient.code);
    setCurrentEditId(ingredient.id);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleDelete = (id: number) => {
    if (!window.confirm("Are you sure you want to delete this ingredient?")) {
      return;
    }

    setLoading(true);
    ingredientsApi
      .deleteIngredient(id)
      .then(() => {
        showSuccess("Ingredient deleted successfully");
        setIngredients((prev) => prev.filter((i) => i.id !== id));
        fetchIngredients();
      })
      .catch(() => setError("Failed to delete ingredient"))
      .finally(() => setLoading(false));
  };

  const handleSave = () => {
    if (!ingredientName || !ingredientCode) return;

    setLoading(true);
    setError(null);

    const apiCall =
      isEditing && currentEditId
        ? ingredientsApi.updateIngredient(currentEditId, {
            id: currentEditId,
            code: ingredientCode,
            name: ingredientName,
          })
        : ingredientsApi.createIngredient({
            code: ingredientCode,
            name: ingredientName,
          });

    apiCall
      .then(() => {
        showSuccess(
          `Ingredient ${isEditing ? "updated" : "created"} successfully`
        );
        fetchIngredients();
        resetForm();
        setModalOpen(false);
      })
      .catch(() =>
        setError(`Failed to ${isEditing ? "update" : "create"} ingredient`)
      )
      .finally(() => setLoading(false));
  };

  const filteredIngredients = ingredients.filter(
    (ingredient) =>
      ingredient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ingredient.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredIngredients.length / entriesPerPage)
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * entriesPerPage;
  const displayedIngredients = filteredIngredients.slice(
    startIndex,
    startIndex + entriesPerPage
  );
  const startEntry = filteredIngredients.length === 0 ? 0 : startIndex + 1;
  const endEntry =
    filteredIngredients.length === 0
      ? 0
      : Math.min(startIndex + displayedIngredients.length, filteredIngredients.length);

  return (
    <PageContainer>
      <div className="d-flex flex-column gap-3">
        <div>
          <PageHeader title="Manage" subTitle="Ingredients" />
        </div>

        <div className="d-flex justify-content-end">
          <Button
            variant="primary"
            onClick={() => setModalOpen(true)}
            className="px-4 shadow-sm"
          >
            <i className="bi bi-plus-lg me-2"></i>
            New Ingredient
          </Button>
        </div>

        {success && (
          <Alert variant="success" onClose={() => setSuccess(null)} dismissible>
            {success}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        <SlidePanel
          open={modalOpen}
          onClose={handleCancel}
          title={isEditing ? "Edit Ingredient" : "Add New Ingredient"}
          footer={
            <>
              <Button variant="secondary" onClick={handleCancel} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={loading || !ingredientName || !ingredientCode}
              >
                <i className="bi bi-save me-1"></i> Save
              </Button>
            </>
          }
        >
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Ingredient Code *</Form.Label>
              <Form.Control
                placeholder="e.g. ING001"
                value={ingredientCode}
                onChange={(e) => setIngredientCode(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                placeholder="Enter ingredient name"
                value={ingredientName}
                onChange={(e) => setIngredientName(e.target.value)}
              />
            </Form.Group>
          </Form>
        </SlidePanel>

        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-3 p-lg-4">
            <TableControls
              entriesPerPage={entriesPerPage}
              onEntriesChange={setEntriesPerPage}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder="Search ingredients..."
              showSearchIcon
              showEntries={false}
            />

            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="small text-uppercase text-secondary">
                  <tr>
                    <th className="border-bottom pb-3 pt-2">Code</th>
                    <th className="border-bottom pb-3 pt-2">Name</th>
                    <th className="border-bottom pb-3 pt-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-4">
                        Loading ingredients...
                      </td>
                    </tr>
                  ) : displayedIngredients.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-4">
                        No ingredients found.
                      </td>
                    </tr>
                  ) : (
                    displayedIngredients.map((ingredient) => (
                      <tr key={ingredient.id}>
                        <td className="py-3 text-body-secondary">{ingredient.code}</td>
                        <td className="py-3 fw-semibold">{ingredient.name}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Button
                              variant="link"
                              size="sm"
                              className="p-1 text-secondary"
                              onClick={() => handleEdit(ingredient)}
                              aria-label="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-1 text-danger"
                              onClick={() => handleDelete(ingredient.id)}
                              aria-label="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 border-top mt-3 pt-3">
              <div className="small text-muted">
                Showing rows {startEntry} to {endEntry} of {filteredIngredients.length}
              </div>

              <Pagination size="sm" className="mb-0 ms-md-auto">
                {Array.from({ length: totalPages }, (_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === safeCurrentPage}
                      onClick={() => setCurrentPage(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}
              </Pagination>
            </div>
          </Card.Body>
        </Card>
      </div>
    </PageContainer>
  );
};

export default Ingredients;
